/**
 * @file reportSlice.js
 * @description Redux state and actions for the Reports module
 */
import { createSlice } from '@reduxjs/toolkit';
import { ReportFilterModel } from 'core/models/ReportFilterModel';
import { ReportConfigModel, ReportConfigStatuses } from 'core/models/ReportConfigModel';
import {
    createReportConfig,
    getReportConfigs,
    getReportTargets,
    getReportUsers,
    updateReportConfig,
} from '../../services/reportService';
import { logger } from 'utils/logger';

const reportSlice = createSlice({
    name: 'report',
    initialState: {
        reportConfigs: [],
        selectedReportConfig: null,
        selectedReportConfigs: [],
        reportTargets: [],
        reportUsers: [],
        reportFilter: new ReportFilterModel(),
        error: null,
        success: null,
        loading: false,
    },
    reducers: {
        selectReportConfig(state, action) {
            if (!action.payload) {
                state.selectedReportConfig = null;
                return;
            }
            const model = new ReportConfigModel(action.payload);
            state.selectedReportConfig = {
                ...model,
                recipients: model.recipients,
            };
        },
        selectReportConfigs(state, action) {
            state.selectedReportConfigs = action.payload ? [...action.payload] : [];
        },
        setReportFilter(state, action) {
            state.reportFilter = action.payload ? { ...action.payload } : new ReportFilterModel();
        },
        clearNotifications(state) {
            state.error = null;
            state.success = null;
        },

        onServiceStart(state) {
            state.success = null;
            state.error = null;
            // state.loading = true;
        },
        onServiceFailure(state, action) {
            logger.log('onServiceFailure', action.payload);
            state.loading = false;
            state.error = action.payload;
        },

        getReportConfigsSuccess(state, action) {
            state.loading = false;
            state.success = { type: action.type, message: 'Get Report Configs Success' };
            state.reportConfigs = [...action.payload];
            state.error = null;
        },
        getReportTargetsSuccess(state, action) {
            state.reportTargets = [...action.payload];
        },
        getReportUsersSuccess(state, action) {
            state.reportUsers = [...action.payload];
        },
        createReportConfigSuccess(state, action) {
            state.loading = false;
            state.success = { type: action.type, message: 'Create Report Config Success' };
            state.reportConfigs = [...state.reportConfigs, action.payload];
            state.error = null;
        },
        updateReportConfigSuccess(state, action) {
            state.loading = false;
            state.success = { type: action.type, message: 'Update Report Config Success' };

            const configs = [...state.reportConfigs];
            const idx = configs.findIndex((val) => val.rcSort === action.payload.rcSort);
            if (idx > -1) {
                configs[idx] = action.payload;
            } else {
                configs.push(action.payload);
            }
            state.reportConfigs = configs;

            if (state.selectedReportConfig?.rcSort === action.payload.rcSort) {
                const model = new ReportConfigModel(action.payload);
                state.selectedReportConfig = {
                    ...model,
                    recipients: model.recipients,
                };
            }
            state.error = null;
        },
        deleteReportConfigSuccess(state, action) {
            state.loading = false;
            state.success = { type: action.type, message: 'Archive Report Config Success' };

            state.reportConfigs = state.reportConfigs.map((val) =>
                val.rcSort === action.payload.rcSort ? { ...val, ...action.payload } : val
            );

            state.error = null;
        },
        batchUpdateReportConfigSuccess(state, action) {
            state.loading = false;
            state.success = { type: action.type, message: 'Report configurations updated', data: action.payload };

            const idMap = new Map(action.payload.map((item) => [item.rcSort, item]));

            state.reportConfigs = state.reportConfigs.map((val) => (idMap.get(val.rcSort) ? { ...val, ...idMap.get(val.rcSort) } : val));

            // selectedReportConfigs only carries { id }, so match the batch results by id, not rcSort
            const updatedById = new Map(action.payload.map((item) => [item.id, item]));

            // if the status is archived then we deselect
            state.selectedReportConfigs = state.selectedReportConfigs.filter((val) => {
                const updated = updatedById.get(val.id);
                return !updated || updated.status !== ReportConfigStatuses.ARCHIVED;
            });

            state.error = null;
        },
    },
});

export const fetchReportConfigs = () => async (dispatch) => {
    try {
        dispatch(onServiceStart());
        const data = await getReportConfigs();
        dispatch(getReportConfigsSuccess(data));
    } catch (err) {
        logger.log('err', err);
        dispatch(onServiceFailure(err));
    }
};

export const fetchReportTargets = () => async (dispatch) => {
    try {
        const data = await getReportTargets();
        dispatch(getReportTargetsSuccess(data));
    } catch (err) {
        logger.log('err', err);
        dispatch(onServiceFailure(err));
    }
};

export const fetchReportUsers = () => async (dispatch) => {
    try {
        const data = await getReportUsers();
        dispatch(getReportUsersSuccess(data));
    } catch (err) {
        logger.log('err', err);
        dispatch(onServiceFailure(err));
    }
};

export const saveReportConfig = (config, isNew = false) => async (dispatch) => {
    try {
        dispatch(onServiceStart());
        const data = isNew ? await createReportConfig(config) : await updateReportConfig(config);
        dispatch(isNew ? createReportConfigSuccess(data) : updateReportConfigSuccess(data));
        return data;
    } catch (err) {
        logger.log('err', err);
        dispatch(onServiceFailure(err));
        throw err;
    }
};


export const deleteReportConfig = (config) => async (dispatch) => {
    try {
        dispatch(onServiceStart());
        await updateReportConfig(config);
        dispatch(deleteReportConfigSuccess(config));
        return config;
    } catch (err) {
        logger.log('err', err);
        dispatch(onServiceFailure(err));
        throw err;
    }
};

export const batchUpdateReportConfig = (configs, status) => async (dispatch) => {
    try {
        dispatch(onServiceStart());
        var updatedConfigs = [];
        for (let i = 0; i < configs.length; i++) {
            const updated = { ...configs[i], status };
            await updateReportConfig(updated);
            updatedConfigs.push(updated);
        }
        dispatch(batchUpdateReportConfigSuccess(updatedConfigs));
        return updatedConfigs;
    } catch (err) {
        logger.log('err', err);
        dispatch(onServiceFailure(err));
        throw err;
    }
};

export const {
    selectReportConfig,
    selectReportConfigs,
    setReportFilter,
    clearNotifications,
    onServiceStart,
    onServiceFailure,
    getReportConfigsSuccess,
    getReportTargetsSuccess,
    getReportUsersSuccess,
    createReportConfigSuccess,
    updateReportConfigSuccess,
    deleteReportConfigSuccess,
    batchUpdateReportConfigSuccess,
} = reportSlice.actions;

export default reportSlice.reducer;
